import { z } from 'zod';

import type { SingleRootDocument } from './types';

// Request and response header of a probe (SingleRoot::DocumentSwap). The response value is the
// answer's kind; a response without it was not answered by the swap (another controller, an
// error page, a redirect outside the controller), so the browser must load the URL itself.
export const PROBE_HEADER = 'X-Single-Root-Probe';
export const LEGACY_PARAM = 'legacy_ui';
// Browsers give up after 20; a Rails redirect chain inside /admin is one or two hops.
export const MAX_PROBE_REDIRECTS = 10;

export type ProbeAnswer =
  | { kind: 'document'; document: SingleRootDocument }
  | { kind: 'redirect'; location: string }
  /** `download`: the action answered with an attachment, so the page stays where it was. */
  | { kind: 'native'; download?: boolean }
  /** 401: the session ended. */
  | { kind: 'unauthenticated' };

export interface ProbeRequest {
  /** The page the user left, sent as Referer like a document navigation (`redirect_back`). */
  referrer: string;
  signal?: AbortSignal;
}

export type Probe = (href: string, request: ProbeRequest) => Promise<ProbeAnswer>;

const linkSchema = z.looseObject({ label: z.string(), href: z.string() });
const documentSchema = z.looseObject({
  href: z.string(),
  railsAction: z.string(),
  status: z.number().int(),
  pageTitle: z.string(),
  breadcrumbsHtml: z.string().nullable(),
  flash: z.array(z.looseObject({ type: z.string(), text: z.string() })),
  legacyAffordances: z.looseObject({ tabs: z.array(linkSchema), actions: z.array(linkSchema) }),
  legacyHref: z.string()
});
const answerSchema = z.discriminatedUnion('kind', [
  z.looseObject({ kind: z.literal('document'), document: documentSchema }),
  z.looseObject({ kind: z.literal('redirect'), location: z.string().min(1) }),
  z.looseObject({ kind: z.literal('native'), download: z.boolean().optional() })
]);

export const fetchProbe =
  (fetchImpl: typeof fetch = async (...args) => fetch(...args)): Probe =>
  async (href, { referrer, signal }) => {
    const response = await fetchImpl(href, {
      credentials: 'same-origin',
      cache: 'no-store',
      // A redirect the swap did not answer (Devise, a routes redirect) is opaque, so native.
      redirect: 'manual',
      referrer,
      signal,
      headers: { Accept: 'application/json', [PROBE_HEADER]: '1' }
    });
    if (response.status === 401) return { kind: 'unauthenticated' };
    if (!response.headers.get(PROBE_HEADER)) return { kind: 'native' };
    const body: unknown = await response.json().catch(() => null);
    const answer = answerSchema.safeParse(body);
    return answer.success ? answer.data : { kind: 'native' };
  };

// A file extension (`/admin/users.csv`) is a download or a non-HTML format: always native.
const FORMAT_EXTENSION = /\.[a-z0-9]+$/i;

/**
 * Whether the shell may show this URL from a probe instead of loading it: a same-origin
 * /admin page (AdminController answers probes), without `?legacy_ui=1` (a request for the
 * legacy page) or a format extension.
 */
export const isProbeable = (url: URL): boolean =>
  url.origin === window.location.origin &&
  (url.pathname === '/admin' || url.pathname.startsWith('/admin/')) &&
  url.searchParams.get(LEGACY_PARAM) !== '1' &&
  !FORMAT_EXTENSION.test(url.pathname);

export type ProbeOutcome =
  | { kind: 'document'; href: string; document: SingleRootDocument }
  | { kind: 'native'; href: string; download: boolean }
  | { kind: 'reload' };

const relativeHref = (url: URL): string => `${url.pathname}${url.search}${url.hash}`;

/**
 * Probes `href` and follows redirects by probing their locations, as the browser would follow
 * them, until the server answers with a document or hands the URL back. A redirect to an
 * admitted route is probed too: its document carries the flash the redirect set, which would
 * otherwise wait in the session for some later page.
 */
export const settleProbe = async (
  probe: Probe,
  href: string,
  request: ProbeRequest
): Promise<ProbeOutcome> => {
  let current = href;
  for (let hop = 0; hop <= MAX_PROBE_REDIRECTS; hop += 1) {
    // eslint-disable-next-line no-await-in-loop -- each hop depends on the previous answer
    const answer = await probe(current, request);
    switch (answer.kind) {
      case 'document':
        return { kind: 'document', href: current, document: answer.document };
      case 'unauthenticated':
        return { kind: 'reload' };
      case 'native':
        return { kind: 'native', href: current, download: answer.download === true };
      default: {
        const target = new URL(answer.location, window.location.origin);
        if (!isProbeable(target)) return { kind: 'native', href: target.href, download: false };
        current = relativeHref(target);
      }
    }
  }
  return { kind: 'native', href: current, download: false };
};
